import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import SEO from "../../components/SEO";
import { blogArticles, blogPageSEO } from "../../data/blogData";

const BlogPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");

  const categories = useMemo(() => {
    const cats = new Set(blogArticles.map((article) => article.category));
    return ["all", ...Array.from(cats)];
  }, []);

  const filteredArticles = useMemo(() => {
    return blogArticles.filter((article) => {
      const matchesCategory =
        selectedCategory === "all" || article.category === selectedCategory;
      const matchesSearch =
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.tags.some((tag) =>
          tag.toLowerCase().includes(searchQuery.toLowerCase()),
        );
      return matchesCategory && matchesSearch;
    });
  }, [selectedCategory, searchQuery]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Blog Dr Youssef Gam",
    description: blogPageSEO.description,
    url: blogPageSEO.canonicalUrl,
    author: {
      "@type": "Person",
      name: "Dr Youssef Gam",
      jobTitle: "Chirurgien Esthétique et Plastique",
    },
    blogPost: blogArticles.map((article) => ({
      "@type": "BlogPosting",
      headline: article.title,
      description: article.excerpt,
      datePublished: article.publishDate,
      author: {
        "@type": "Person",
        name: article.author,
      },
      url: `https://www.dryoussefgam.com/blog/${article.slug}`,
    })),
  };

  // Featured article is the latest one
  const featuredArticle = filteredArticles[filteredArticles.length - 1];
  const restArticles = filteredArticles.slice(0, -1);

  return (
    <>
      <SEO
        title={blogPageSEO.title}
        description={blogPageSEO.description}
        keywords={blogPageSEO.keywords}
        canonicalUrl={blogPageSEO.canonicalUrl}
        ogImage={blogPageSEO.ogImage}
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center bg-lightViolet overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, #000 0.5px, transparent 0)`,
              backgroundSize: "32px 32px",
            }}
          />
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 py-32">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
          >
            <motion.span
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-block text-xs font-primaryMedium tracking-[0.3em] text-gray600 uppercase mb-8"
            >
              Actualités & Conseils
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-4xl md:text-7xl font-primaryMedium text-darkB mb-6 leading-[0.95]"
            >
              Le Blog
            </motion.h1>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: "80px" }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="h-[1px] bg-darkB mb-8"
            />
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="text-lg md:text-xl text-gray600 max-w-2xl font-primaryLight leading-relaxed"
            >
              Innovations en chirurgie esthétique, conseils experts et
              actualités médicales par le Dr Youssef Gam
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-0 z-30 bg-lightB border-b border-gray200 py-4 px-4 md:px-8">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex flex-wrap gap-1.5 justify-center md:justify-start">
            {categories.map((category) => (
              <motion.button
                key={category}
                onClick={() => setSelectedCategory(category)}
                whileHover={{ y: -1 }}
                whileTap={{ scale: 0.97 }}
                className={`px-5 py-2 text-sm font-primaryMedium transition-all duration-300 border ${
                  selectedCategory === category
                    ? "bg-darkB text-lightB border-darkB"
                    : "text-gray600 border-gray200 hover:border-darkB hover:text-darkB"
                }`}
              >
                {category === "all" ? "Tous" : category}
              </motion.button>
            ))}
          </div>

          <div className="relative w-full md:w-64">
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray200 focus:border-darkB outline-none transition-all font-primaryLight text-sm"
            />
            <svg
              className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-consent"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>
      </section>

      {/* Featured Article */}
      {featuredArticle && filteredArticles.length > 1 && (
        <section className="py-16 px-4 md:px-8 max-w-7xl mx-auto">
          <Link to={`/blog/${featuredArticle.slug}`}>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="group grid grid-cols-1 lg:grid-cols-2 gap-0 bg-lightB border border-gray200 overflow-hidden"
            >
              <div className="relative h-72 md:h-96 lg:h-full overflow-hidden">
                {featuredArticle.thumbnailImage ? (
                  <motion.img
                    src={featuredArticle.thumbnailImage}
                    alt={featuredArticle.title}
                    className="w-full h-full object-cover"
                    whileHover={{ scale: 1.03 }}
                    transition={{ duration: 0.6 }}
                  />
                ) : (
                  <div className="w-full h-full bg-lightViolet" />
                )}
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1.5 bg-darkB text-lightB text-xs font-primaryMedium uppercase tracking-wider">
                    À la une
                  </span>
                </div>
              </div>
              <div className="p-8 md:p-12 lg:p-16 flex flex-col justify-center">
                <span className="text-xs font-primaryMedium tracking-[0.2em] text-consent uppercase mb-4">
                  {featuredArticle.category} — {featuredArticle.readTime}
                </span>
                <h2 className="text-3xl md:text-4xl font-primaryMedium text-darkB mb-4 leading-tight group-hover:opacity-70 transition-opacity">
                  {featuredArticle.title}
                </h2>
                <p className="text-gray600 font-primaryLight leading-relaxed mb-8 line-clamp-3">
                  {featuredArticle.excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {featuredArticle.authorImage && (
                      <img
                        src={featuredArticle.authorImage}
                        alt={featuredArticle.author}
                        className="w-9 h-9 rounded-full object-cover"
                      />
                    )}
                    <div>
                      <p className="text-darkB font-primaryMedium text-sm">
                        {featuredArticle.author}
                      </p>
                      <p className="text-consent text-xs font-primaryLight">
                        {formatDate(featuredArticle.publishDate)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-darkB font-primaryMedium text-xs uppercase tracking-wider group-hover:gap-4 transition-all">
                    <span>Lire</span>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M17 8l4 4m0 0l-4 4m4-4H3"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </motion.div>
          </Link>
        </section>
      )}

      {/* Articles Grid */}
      <section className="py-8 pb-24 px-4 md:px-8 max-w-7xl mx-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={selectedCategory + searchQuery}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {(filteredArticles.length > 1
              ? restArticles
              : filteredArticles
            ).map((article, index) => (
              <motion.article
                key={article.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="group"
              >
                <Link to={`/blog/${article.slug}`}>
                  <div className="bg-lightB transition-all duration-500 border border-transparent hover:border-gray200">
                    {/* Image */}
                    <div className="relative h-64 overflow-hidden bg-lightViolet">
                      {article.thumbnailImage ? (
                        <motion.img
                          src={article.thumbnailImage}
                          alt={article.title}
                          className="w-full h-full object-cover"
                          whileHover={{ scale: 1.05 }}
                          transition={{ duration: 0.5 }}
                        />
                      ) : (
                        <div className="w-full h-full bg-lightViolet flex items-center justify-center">
                          <span className="text-5xl opacity-30">
                            {article.sections[0]?.icon || "🏥"}
                          </span>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-darkB/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      <span className="absolute top-3 left-3 px-3 py-1 bg-lightB/90 text-darkB text-[10px] font-primaryMedium uppercase tracking-wider backdrop-blur-sm">
                        {article.category}
                      </span>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      <div className="flex items-center gap-2 mb-3 text-xs text-consent font-primaryLight">
                        <time dateTime={article.publishDate}>
                          {formatDate(article.publishDate)}
                        </time>
                        <span className="w-1 h-1 rounded-full bg-consent" />
                        <span>{article.readTime}</span>
                      </div>

                      <h2 className="text-xl font-primaryMedium text-darkB mb-3 group-hover:opacity-70 transition-opacity line-clamp-2 leading-snug">
                        {article.title}
                      </h2>

                      <p className="text-gray600 font-primaryLight text-sm leading-relaxed mb-5 line-clamp-2">
                        {article.excerpt}
                      </p>

                      <div className="flex items-center gap-2 text-darkB font-primaryMedium text-xs uppercase tracking-wider group-hover:gap-3 transition-all">
                        <span>Lire l'article</span>
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M17 8l4 4m0 0l-4 4m4-4H3"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </motion.div>
        </AnimatePresence>

        {/* No Results */}
        {filteredArticles.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-24"
          >
            <div className="text-6xl mb-6 opacity-20">🔍</div>
            <h3 className="text-2xl font-primaryMedium text-darkB mb-3">
              Aucun article trouvé
            </h3>
            <p className="text-consent font-primaryLight">
              Essayez de modifier vos critères de recherche
            </p>
          </motion.div>
        )}
      </section>
    </>
  );
};

export default BlogPage;
